<template>
  <div align="left">
    <b-table-simple>
      <b-tr>
        <b-td width="10%">Size in bits:</b-td>
        <b-td width="10%"><b-form-input v-model="size" type="number" :min="0" :max="128"/></b-td>
        <b-td><b-form-input v-model="decimalValue" :state="decimalValid" placeholder="Enter an integer value"/></b-td>
        <b-td>
          <b-form-checkbox v-model="complement" name="complment-switch" switch>
            Show complement
          </b-form-checkbox>
        </b-td>
      </b-tr>
    </b-table-simple><p/>

    <b-card title="Signed Magnitude">
      <div id="signed-magnitude" class="conv-number" :hidden="!decimalValid">{{this.signedMagnitude}}</div>
      <b-popover custom-class="conv-popover" target="signed-magnitude" triggers="hover" placement="right">
        <template #title>Conversion to Signed Magnitude</template>
        <div class="conv-popover">(Sign = {{Math.sign(this.decimalInt) === 1 ? '0' : '1'}}) + (Magnitude = {{this.magnitude}}) = {{this.signedMagnitude}}</div>
      </b-popover>
      <div id="signed-magnitude-comp" class="conv-number" :hidden="!decimalValid || !complement">{{this.signedMagnitudeComplement}}</div>
      <b-popover custom-class="conv-popover" target="signed-magnitude-comp" triggers="hover" placement="right">
        <template #title>Conversion to Signed Magnitude</template>
        <div class="conv-popover">(Sign = {{Math.sign(-this.decimalInt) === 1 ? '0' : '1'}}) + (Magnitude = {{this.magnitude}}) = {{this.signedMagnitudeComplement}}</div>
      </b-popover>
    </b-card><p/>
    <b-card title="1s Complement">
      <div id="ones-complement" class="conv-number" :hidden="!decimalValid">{{this.onesComplement}}</div>
      <b-popover custom-class="conv-popover" target="ones-complement" triggers="hover" placement="right">
        <template #title>Conversion to Ones Complement</template>
        <div class="conv-popover">The value is {{this.decimalInt >= 0 ? 'Non-negative' : 'Negative'}}, therefore {{this.decimalInt >= 0 ? 'no conversion' : 'the ones complement is !(' + this.binaryString + ') = ' + this.onesComplement}}</div>
      </b-popover>
      <div id="ones-complement-comp" class="conv-number" :hidden="!decimalValid || !complement">{{this.onesComplementComplement}}</div>
      <b-popover custom-class="conv-popover" target="ones-complement-comp" triggers="hover" placement="right">
        <template #title>Conversion to Ones Complement</template>
        <div class="conv-popover">The value is {{(this.decimalInt < 0) ? 'Non-negative' : 'Negative'}}, therefore {{this.decimalInt < 0 ? 'no conversion' : 'the ones complement is !(' + this.binaryString + ') = ' + this.onesComplementComplement}}</div>
      </b-popover>
    </b-card><p/>
    <b-card title="2s Complement">
      <div id="twos-complement" class="conv-number" :hidden="!decimalValid">{{this.twosComplement}}</div>
      <b-popover custom-class="conv-popover" target="twos-complement" triggers="hover" placement="right">
        <template #title>Conversion to Twos Complement</template>
        <div class="conv-popover">The value is {{this.decimalInt >= 0 ? 'Non-negative' : 'Negative'}}, therefore {{this.decimalInt >= 0 ? 'no conversion' : 'the twos complement is !(' + this.binaryString + ') + 1 = ' + this.twosComplement}}</div>
      </b-popover>
      <div id="twos-complement-comp" class="conv-number" :hidden="!decimalValid || !complement">{{this.twosComplementComplement}}</div>
      <b-popover custom-class="conv-popover" target="twos-complement-comp" triggers="hover" placement="right">
        <template #title>Conversion to Twos Complement</template>
        <div class="conv-popover">The value is {{(this.decimalInt < 0) ? 'Non-negative' : 'Negative'}}, therefore {{this.decimalInt < 0 ? 'no conversion' : 'the ones complement is !(' + this.binaryString + ') + 1 = ' + this.twosComplementComplement}}</div>
      </b-popover>
    </b-card>
  </div>
</template>

<script>
import {Numbers, fillBinary} from '@/util/numbers'

export default {
  name: 'representations',
  data () {
    return {
      size: 8,
      complement: false
    }
  },
  mixins: [Numbers],
  methods: {
    computeOnesComplement: function (value) {
      let sign = Math.sign(value)
      let bits = value.toString(2)
      if (sign !== -1) {
        bits = '0'.repeat(this.size - bits.length) + bits
      } else {
        bits = ((~Math.abs(this.decimalInt)) >>> 0).toString(2)
        bits = bits.substring(bits.length - this.size)
      }
      return bits
    },
    computeTwosComplement: function (value) {
      let sign = Math.sign(value)
      let bits = (value >>> 0).toString(2)
      if (bits.length < this.size) {
        bits = (sign !== -1 ? '0' : '1').repeat(this.size - bits.length) + bits
      } else if (bits.length > this.size) {
        bits = bits.substring(bits.length - this.size)
      }
      return bits
    }
  },
  computed: {
    signedMagnitude: function () {
      let sign = Math.sign(this.decimalInt)
      let bits = this.toBinary(sign * this.decimalInt)
      let bitsLength = bits.length

      if (bitsLength >= this.size) {
        this.size = bitsLength + 1
      }

      return (sign !== -1 ? '0' : '1') + '0'.repeat(this.size - bitsLength - 1) + bits
    },
    signedMagnitudeComplement: function () {
      return (this.signedMagnitude[0] === '1' ? '0' : '1') + this.signedMagnitude.substring(1)
    },
    onesComplement: function () {
      return this.computeOnesComplement(this.decimalInt)
    },
    onesComplementComplement: function () {
      return this.computeOnesComplement(-1 * (this.decimalInt === 0 ? 1 : this.decimalInt))
    },
    twosComplement: function () {
      return this.computeTwosComplement(this.decimalInt)
    },
    twosComplementComplement: function () {
      return this.computeTwosComplement(-1 * this.decimalInt)
    },
    magnitude: function () {
      return fillBinary(Math.abs(this.decimalInt), this.size - 1)
    },
    binaryString: function () {
      return fillBinary(Math.abs(this.decimalInt), this.size)
    }
  }
}
</script>

<style scoped>
.conv-number {
  font-family: monospace;
  width: fit-content;
  padding-right: 15px;
  font-size: x-large;
}

.conv-popover {
  max-width: none;
  white-space: nowrap;
}
</style>


// WEBPACK FOOTER //
// src/components/representations.vue